import * as React from 'react'
import Dropzone from 'react-dropzone'
import Select from 'react-select'
import { Button, Input } from 'reactstrap'
import { Channel, StreamChat } from 'stream-chat'
import { useChatContext } from 'stream-chat-react'
import CheckBox from '../CheckBox'
import { createChannel, handleFileUpload, updateChannel } from './actions'
import ChatModal from './ChatModal'

import './ChatModal.scss'
import './EditChannelModal.scss'

type IComponentProps = {
  isOpen: boolean
  client: StreamChat
  channel: Channel | null
  action?: 'addMembers'
  onClose: () => void
  onError: (err: Error) => void
}

const EditChannelModal = ({
  isOpen,
  client,
  channel,
  action,
  onClose,
  onError,
}: IComponentProps) => {
  const [users, setUsers] = React.useState<{ label: string; value: string }[]>([])
  const [channelImage, setChannelImage] = React.useState<string>(
    channel?.data?.image as string ?? ''
  )
  const [channelName, setChannelName] = React.useState<string>(
    channel?.data?.name ?? ''
  )
  const [selectedUsers, setSelectedUsers] = React.useState<
    { label: string; value: string }[]
  >([])
  const [selectedUsersLoading, setSelectedUsersLoading] = React.useState<boolean>(false)
  const [isPublic, setIsPublic] = React.useState<boolean>(
    (channel?.data?.public ?? false) as boolean
  )

  const { setActiveChannel } = useChatContext()

  React.useEffect(() => {
    if (channel && !channel.data?.public) {
      setSelectedUsersLoading(true)
      channel
        .queryMembers({})
        .then(response => {
          setSelectedUsers(
            response.members
              .filter(member => member.user && member.user.id !== client.userID)
              .map(member => ({
                label:
                  member.user!.name ?? member.user!.username ?? member.user!.id,
                value: member.user!.id
              }))
          )
        })
        .catch(error => {
          onError(new Error((error as Error).message ?? 'Failed to load channel members'))
        })
        .finally(() => {
          setSelectedUsersLoading(false)
        })
    }
  }, [channel, client, onError])

  // Query a list of users using autocomplete
  const searchUsers = React.useCallback((query: string) => {
    client
      .queryUsers({
        name: { $autocomplete: query },
        banned: false
      })
      .then((response) => {
        const results = response.users
          .filter(user => user.id !== client.userID)
          .map(user => ({
            label: user.name ?? user.username ?? user.id,
            value: user.id
          }))
        setUsers(results)
      })
      .catch((error) => {
        onError(new Error((error as Error).message ?? 'Failed to search for users'))
      })
    },
    [client, onError]
  )

  const submitWord = React.useMemo(() => {
    return channel ? 'Save' : 'Create'
  }, [channel])

  const submit = React.useCallback(async () => {
    const name = channelName.trim()

    try {
      // Check if we're editing a channel
      if (channel) {
        const response = await updateChannel(channel.id!, {
          name,
          image: channelImage,
          desiredMembers: selectedUsers.map(user => user.value),
          isPublic,
        })
        if (!response.success) {
          throw new Error(response.message ?? 'Failed to update channel')
        }
      } else {
        const response = await createChannel({
          name,
          image: channelImage,
          desiredMembers: selectedUsers.map(user => user.value),
          isPublic,
        })
        if (!response.success) {
          throw new Error(response.message ?? 'Failed to create channel')
        }

        // Focus the new channel if our client receives it in time.
        const chan = client.activeChannels[`messaging:${response.channelId}`]
        if (chan) {
          setActiveChannel(chan)
        }
      }
    } catch (error) {
      onError(error as Error)
    }
  }, [channel, selectedUsers, channelName, channelImage, isPublic, client, onError, setActiveChannel])

  return (
    <ChatModal isOpen={isOpen} onClose={onClose} className='edit-channel-modal'>
      <div className='w-100 mb-4 edit-channel-modal-container'>
        <Dropzone
          onDrop={async (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0) {
              return
            } else if (acceptedFiles.length > 1) {
              onError(new Error('Only one image can be uploaded'))
              return
            }
            try {
              const file = await handleFileUpload(acceptedFiles[0])
              setChannelImage(file.file)
            } catch (error) {
              onError(error as Error)
            }
          }}
        >
          {({getRootProps, getInputProps}) => (
            <div className='edit-channel-modal-image-dropzone' {...getRootProps()} >
              {channelImage ? (
                <img src={channelImage} className='edit-channel-modal-image' />
              ) : (
                <span className='text-center'>Optional Channel Icon</span>
              )}
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
        {channelImage && (
          <button
            className='btn btn-outline-secondary edit-channel-modal-image-remove-button mt-2'
            onClick={() => setChannelImage('')}
          >
            Remove Image
          </button>
        )}
        {channel && channel.id && <CopyableID id={channel.id} />}
        <Input
          className='mt-3'
          value={channelName}
          placeholder='Channel Name'
          onChange={e => setChannelName(e.target.value)}
        />
        {!isPublic && (
          <Select
            isMulti
            autoFocus={action === 'addMembers'}
            className='w-100 mt-3'
            noOptionsMessage={() => 'No matching users'}
            options={users}
            value={selectedUsers}
            isDisabled={isPublic}
            isLoading={selectedUsersLoading}
            placeholder='Add people...'
            onChange={newValue => {
              setSelectedUsers(
                newValue.map(user => ({
                  label: user.label,
                  value: user.value
                }))
              )
            }}
            onInputChange={newValue => {
              // Only search for users if the input contains only letters and spaces
              if (!newValue || /^[a-zA-Z\s]+$/.test(newValue) == false) {
                setUsers([])
                return
              }
              searchUsers(newValue)
            }}
          />
        )}
        {client.user?.role === 'admin' && (
          <CheckBox
            className='mt-3'
            value={isPublic}
            onChange={newValue => setIsPublic(newValue)}
          >
            Public
          </CheckBox>
        )}
      </div>
      <div className='d-flex justify-content-end w-100'>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color='primary'
          className='ml-3'
          onClick={() => submit().then(() => onClose())}
          disabled={(!isPublic && selectedUsers.length < 1) || !channelName.trim()}
        >
          {`${submitWord} Channel`}
        </Button>
      </div>
    </ChatModal>
  )
}

export default EditChannelModal

const CopyableID = ({ id }: { id: string }) => {
  const [copied, setCopied] = React.useState(false)

  const copyId = React.useCallback(() => {
    navigator.clipboard.writeText(id)
    setCopied(true)
  }, [id])

  React.useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  return (
    <div className='d-flex align-items-center mt-3'>
      <span className='p-0'>ID: {id}</span>
      <button className={`btn btn-sm ml-2 ${copied ? 'btn-primary active' : 'btn-outline-secondary'}`} aria-pressed={copied} onClick={copyId}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  )
}
